<template>
  <div class="card-modern">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="card-title-modern">Asset IT Tanpa Asset Number</h3>
        <small class="text-muted">
          Daftar Aset IT &amp; Infrastruktur IT yang belum memiliki asset number.
        </small>
      </div>
    </div>
  
    <div class="d-flex align-items-end justify-content-end gap-2 mb-3 flex-wrap">
      <button class="btn btn-sm bg-success-subtle border border-success-subtle text-success-emphasis"
        style="border-radius: 8px" :disabled="exporting" @click="doExport">
        <span v-if="exporting">
          <span class="spinner-border spinner-border-sm me-1"></span>Mengunduh...
        </span>
        <span v-else><i class="bi bi-file-earmark-excel me-1"></i>Export Excel</span>
      </button>
  
      <div style="width: 200px">
        <label class="form-label small text-muted mb-1">Cari</label>
        <input v-model="filters.search" class="form-control" style="border-radius: 8px"
          @keyup.enter="fetchData(1)" />
      </div>
  
      <div style="width: 180px">
        <label class="form-label small text-muted mb-1">Tipe Sumber</label>
        <select v-model="filters.source_type" class="form-select" style="border-radius: 8px">
          <option value="">Semua</option>
          <option value="ASET_IT">Aset IT</option>
          <option value="INFRASTRUKTUR_IT">Infrastruktur IT</option>
        </select>
      </div>
  
      <button class="button-modern" @click="fetchData(1)">
        <i class="bi bi-search me-1"></i>Cari
      </button>
      <button class="btn btn-sm bg-light border text-dark" style="border-radius: 8px; height: 38px" @click="resetFilter">
        Reset
      </button>
    </div>
  
    <div v-if="!loading" class="mb-3">
      <span class="badge-modern badge-warning">
        {{ pagination.total }} item belum punya asset number
      </span>
    </div>
  
    <div class="modern-table-wrapper">
      <table class="table-modern">
        <thead>
          <tr>
            <th>No</th>
            <th>Tipe</th>
            <th>Device Type</th>
            <th>Merk / Tipe</th>
            <th>Departemen</th>
            <th>Site</th>
            <th class="text-center">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="7" class="text-center py-4">Memuat...</td>
          </tr>
          <tr v-else-if="!data.length">
            <td colspan="7" class="text-center py-4 text-muted">Tidak ada data</td>
          </tr>
          <tr v-for="(item, idx) in data" :key="item.source_type + '-' + item.reference_id" v-else>
            <td>{{ rowNum(idx) }}</td>
            <td>
              <span class="badge-modern"
                :class="item.source_type === 'INFRASTRUKTUR_IT' ? 'badge-info' : 'badge-primary'">
                {{ sourceLabel(item.source_type) }}
              </span>
            </td>
            <td>{{ item.device_type_name || '-' }}</td>
            <td>{{ [item.merk, item.tipe].filter(Boolean).join(' / ') || '-' }}</td>
            <td>{{ item.division_name || '-' }}</td>
            <td>{{ item.site_name || '-' }}</td>
            <td class="text-center">
              <button class="btn-icon-hover icon-primary" title="Detail" @click="openDetail(item)">
                <i class="bi bi-eye"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  
    <div class="d-flex justify-content-between align-items-center mt-3">
      <small class="text-muted">Total {{ pagination.total }} data</small>
      <div class="d-flex align-items-center gap-2">
        <select v-model.number="pagination.limit" class="form-select form-select-sm" style="width: 70px"
          @change="fetchData(1)">
          <option v-for="o in [10, 25, 50, 100]" :key="o" :value="o">{{ o }}</option>
        </select>
        <nav>
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: pagination.page <= 1 }">
              <button class="page-link" @click="fetchData(pagination.page - 1)">‹</button>
            </li>
            <li class="page-item disabled">
              <span class="page-link">{{ pagination.page }} / {{ totalPages }}</span>
            </li>
            <li class="page-item" :class="{ disabled: pagination.page >= totalPages }">
              <button class="page-link" @click="fetchData(pagination.page + 1)">›</button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import reportService from '@/services/reportAssetNumberService';

const router = useRouter();
const data = ref([]);
const loading = ref(false);
const exporting = ref(false);
const filters = reactive({ search: '', source_type: '' });
const pagination = reactive({ page: 1, limit: 10, total: 0 });
const totalPages = computed(() =>
  Math.max(1, Math.ceil(pagination.total / pagination.limit))
);

const buildParams = (extra = {}) => {
  const p = { page: pagination.page, limit: pagination.limit, ...extra };
  if (filters.search) p.search = filters.search;
  if (filters.source_type) p.source_type = filters.source_type;
  return p;
};

const fetchData = async (page = 1) => {
  if (page < 1) return;
  loading.value = true;
  pagination.page = page;
  try {
    const res = await reportService.getList(buildParams({ page }));
    data.value = res.data.data || [];
    pagination.total = res.data.pagination?.total || 0;
  } catch (e) {
    console.error(e);
    alert('Gagal memuat asset tanpa number');
    data.value = [];
  } finally {
    loading.value = false;
  }
};

const resetFilter = () => {
  filters.search = '';
  filters.source_type = '';
  fetchData(1);
};

const doExport = async () => {
  exporting.value = true;
  try {
    const res = await reportService.exportExcel(buildParams({ page: 1, limit: 0 }));
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Asset_Tanpa_Number.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    alert('Gagal export');
  } finally {
    exporting.value = false;
  }
};

const rowNum = (i) => (pagination.page - 1) * pagination.limit + i + 1;

const sourceLabel = (type) =>
  type === 'INFRASTRUKTUR_IT' ? 'Infrastruktur IT' : 'Aset IT';

const openDetail = (item) => {
  const from = router.currentRoute.value.fullPath;

  if (item.source_type === 'INFRASTRUKTUR_IT') {
    const id = item.parent_id || item.reference_id;
    router.push({
      path: `/asset/infrastructure-it/detail/${id}`,
      query: { from },
    });
  } else {
    router.push({
      path: `/asset/asset-it/detail/${item.reference_id}`,
      query: { from },
    });
  }
};

onMounted(() => fetchData(1));
</script>